<template>
  <v-container class="mt-5">
    <Alert />

    <p class="red--text font-italic caption pa-3">
      <v-icon color="red" small>mdi-alert</v-icon>
      All documents should be scanned certified colour copy. Certification to be
      done by the Commissioner of Oaths whose names and address are fully
      displayed on the Rubber Stamp
    </p>

    <CertificationTable
      title="ACADEMIC QUALIFICATIONS"
      :headers="professionalHeaders"
      :items="professionalQualifications"
      :loading="$store.getters['loading']"
      @edit="update"
      @delete="remove"
      @download="download"
    >
      <template #actions>
        <v-menu offset-y>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              outlined
              color="primary"
              dark
              class="mb-2"
              v-bind="attrs"
              v-on="on"
            >
              Add Certification
            </v-btn>
          </template>
          <v-list>
            <v-list-item-group>
              <v-list-item
                @click="openDialog({ locale: 'Local', type: 'Professional' })"
              >
                <v-list-item-title>Local University</v-list-item-title>
              </v-list-item>
              <v-list-item
                @click="openDialog({ locale: 'Foreign', type: 'Professional' })"
              >
                <v-list-item-title>Foreign University</v-list-item-title>
              </v-list-item>
            </v-list-item-group>
          </v-list>
        </v-menu>
      </template>
    </CertificationTable>

    <CertificationTable
      title="OTHER QUALIFICATIONS (e.g O Level)"
      :headers="educationalHeaders"
      :items="educationalQualifications"
      :loading="$store.getters['loading']"
      @edit="update"
      @delete="remove"
      @download="download"
    >
      <template #actions>
        <v-btn
          outlined
          color="primary"
          dark
          class="mb-2"
          @click="openDialog({ locale: 'Local', type: 'Educational' })"
        >
          Add Other
        </v-btn>
      </template>
    </CertificationTable>

    <CertificationTable
      title="AWARDS AND RECOGNITIONS"
      :headers="recognitionHeaders"
      :items="recognitions"
      :loading="$store.getters['loading']"
      @edit="update"
      @delete="remove"
      @download="download"
    >
      <template #actions>
        <v-btn
          outlined
          color="primary"
          dark
          class="mb-2"
          @click="openDialog({ locale: 'Local', type: 'Recognition' })"
        >
          Add Recognition
        </v-btn>
      </template>
    </CertificationTable>

    <CertificationDialog v-model="dialog" :edit="edit" :title="formTitle">
      <CertificationForm
        ref="form"
        :minorSettings="minorSettings"
        :certificationCodes="certificationCodes"
        :qualificationCodes="qualificationCodes"
        :countries="countries"
        @save="save"
        @close="closeDialog"
        @download="download"
      />
    </CertificationDialog>
  </v-container>
</template>

<script>
import Alert from "@/components/Alert.vue";
import FileMixin from "@/mixins/FileMixin";
import { AuthService } from "@/modules/auth";
import { helper } from "@/utils";
import CertificationTable from "@/modules/profile/components/certification/components/CertificationTable.vue";
import CertificationDialog from "@/modules/profile/components/certification/components/CertificationDialog.vue";
import CertificationForm from "@/modules/profile/components/certification/components/CertificationForm.vue";
import SetupMixin from "@/modules/setup/SetupMixin";

export default {
  name: "Certification",
  components: {
    CertificationForm,
    CertificationDialog,
    CertificationTable,
    Alert,
  },
  mixins: [FileMixin, SetupMixin],
  data() {
    return {
      professionalHeaders: [
        { text: "Institution", value: "Institution" },
        { text: "Start Date", value: "StartDate" },
        { text: "Graduation Date", value: "GraduationDate" },
        { text: "Certificate Awarded", value: "CertificateAwarded" },
        { text: "Area Of Specialization", value: "AreaOfSpecialization" },
        { text: "Attachment", value: "attachment", sortable: false },
        { text: "Actions", value: "actions", sortable: false },
      ],
      educationalHeaders: [
        { text: "Institution", value: "Institution" },
        { text: "Start Date", value: "StartDate" },
        { text: "End Date", value: "EndDate" },
        { text: "Certificate Awarded", value: "CertificateAwarded" },
        { text: "Attachment", value: "attachment", sortable: false },
        { text: "Actions", value: "actions", sortable: false },
      ],
      recognitionHeaders: [
        { text: "Examining Body", value: "ExaminingBody" },
        { text: "Start Date", value: "StartDate" },
        { text: "Graduation Date", value: "GraduationDate" },
        { text: "Certificate Awarded", value: "CertificateAwarded" },
        { text: "Qualification", value: "QualificationDescription" },
        { text: "Attachment", value: "attachment", sortable: false },
        { text: "Actions", value: "actions", sortable: false },
      ],
      dialog: false,
      edit: false,
      formType: "Professional",
    };
  },

  mounted() {
    this.$store.dispatch("Profile/getCertifications");
    this.getSetups([
      "getQualificationCodes",
      "getCertificationCodes",
      "getCountries",
    ]);
  },

  computed: {
    certifications() {
      return this.$store.getters["Profile/certifications"];
    },
    professionalQualifications() {
      return this.certifications.filter((c) => c.Type === "Professional");
    },
    educationalQualifications() {
      return this.certifications.filter((c) => c.Type === "Educational");
    },
    recognitions() {
      return this.certifications.filter((c) => c.Type === "Recognition");
    },
    formTitle() {
      return this.formType === "Recognition" ? "Recognition" : "Qualification";
    },
  },

  methods: {
    openDialog(params) {
      this.edit = false;
      this.dialog = true;
      this.$nextTick(() => {
        this.$refs.form.reset();
        this.$refs.form.setData({
          Category: params.locale,
          Type: params.type,
        });
      });
    },

    closeDialog() {
      this.dialog = false;
    },

    update(item) {
      this.edit = true;
      this.dialog = true;

      this.$nextTick(() => {
        this.$refs.form.setData(item);
      });
    },

    async save(data) {
      if (data.UniversityCode === "OTHER") {
        delete data.UniversityCode;
      }

      await this.$store.dispatch(
        `Profile/${this.edit ? "updateCertification" : "addCertification"}`,
        helper.prepareFormData({
          ...data,
          transcript: JSON.stringify(data.transcript),
          ProfileID: AuthService.user.profile.ProfileID,
          EntryNo: data.EntryNo ? data.EntryNo : this.certifications.length + 1,
        })
      );

      this.closeDialog();
    },

    remove(item) {
      this.$confirm.show({
        title: "Delete this Certification?",
        text: `Are you sure you want to delete?`,
        onConfirm: () => {
          this.$store.dispatch("Profile/removeCertification", { ...item });
        },
      });
    },

    download(file) {
      this.downloadFile(file);
    },
  },
};
</script>

<style scoped></style>
